import { useNavigate, useParams } from "react-router";
import { useInstallation } from "../../hooks/useInstallation";
import { Button, Icon, Tab, TabList, TabPanel, Tabs } from "@gds-react/core";
import { ArrowNavigationLeftOutlined } from "@gds-react/icons";
import {
  BackButtonWrapper,
  CreateButtonWrapper,
  HeaderTiles,
  ListTile,
  ScreenHeader,
  Spacer,
} from "./InstallationPage.styles";
import BreadCrumb from "../../components/Breadcrumb";
import Screen from "../../components/Screen";
import { routes } from "../../utils/routeHelper";
import { useAccountList } from "../../hooks/useAccountList";
import { useAccount } from "../../hooks/useAccount";
import InstallationDetailTile from "../../components/InstallationDetailTile";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceTabTypeEnum } from "../../types/DeviceTabTypeEnum";
import { useStore } from "../../store/store";
import CoreSetupInstallation from "@gf-core/widgets/widgets/CoreSetupInstallation";
import CoreDeviceRule from "@gf-core/widgets/widgets/CoreDeviceRule";
import { CorePermissionsEnum } from "@gf-core/widgets/types/CorePermissionsEnum";
import { getCallbackUrl } from "../../utils/helpers/getCallbackUrl";
import { CoreDeviceListRef } from "@gf-core/widgets/types/CoreDeviceListRef";
import DevicesTab from "./Tabs/DevicesTab";
import ReportedStateTab from "./Tabs/ReportedStateTab";

export default function InstallationPage() {
  const { t } = useTranslation();
  const deviceListRef = useRef<CoreDeviceListRef>(null);
  const [show, setShow] = useState(false);
  const [selectedTab, setSelectedTab] = useState("devices");
  const navigate = useNavigate();
  const { data: accounts } = useAccountList(true);
  const { installationId, accountId } = useParams();
  const { data: account } = useAccount(accountId, true);
  const { data: installation, isLoading: isLoadingInstallation } =
    useInstallation(accounts?.model[0]?.id ?? "", installationId, true);
  const setActiveMenuId = useStore.useSetActiveMenuId();
  const [showWibutlerRuleWidget, setShowWibutlerRuleWidget] = useState(false);
  const [ruleState, setRuleState] = useState<{
    authenticationCode: string | undefined;
    installationId: string | undefined;
    deviceTwinId: string | undefined;
  }>({
    authenticationCode: undefined,
    installationId: undefined,
    deviceTwinId: undefined,
  });
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");

  useEffect(() => {
    setActiveMenuId(installationId);
  }, [installationId]);

  useEffect(() => {
    if (code && state && !isLoadingInstallation) {
      const data = localStorage.getItem("@gf-core/widgets");
      const parsedData = data ? JSON.parse(data) : null;
      const rules = parsedData ? parsedData.state.wibutlerRules : {};
      const rule = state && rules ? rules[state] : null;

      setRuleState({
        authenticationCode: code,
        deviceTwinId: rule?.deviceTwinId,
        installationId: rule?.installationId,
      });
      setShowWibutlerRuleWidget(true);
    }
  }, [code, state, isLoadingInstallation]);

  return (
    <>
      <Screen isAnimated>
        <ScreenHeader>
          <BackButtonWrapper>
            <Button
              onClick={() => navigate(routes.account.set(accountId ?? ""))}
            >
              <Icon src={<ArrowNavigationLeftOutlined />} />
              {t("devicePage.backButton")}
            </Button>
          </BackButtonWrapper>

          <BreadCrumb
            list={installation?.meta?.breadcrumbs ?? []}
            isLoading={isLoadingInstallation}
          />
          <Spacer />

          <CreateButtonWrapper>
            <Button
              disabled={isLoadingInstallation}
              onClick={() => {
                setShow(true);
              }}
            >
              {t("installationPage.setupInstallationButton")}
            </Button>
          </CreateButtonWrapper>
        </ScreenHeader>
        <HeaderTiles>
          <InstallationDetailTile
            installation={installation?.model}
            account={account?.model}
            isLoading={isLoadingInstallation}
          />
        </HeaderTiles>

        <ListTile>
          <Tabs
            value={selectedTab}
            onValueChange={(value) => {
              setSelectedTab(value);
            }}
          >
            <TabList>
              <Tab value="devices">{t("devicePage.tabs.devices")}</Tab>
              <Tab value="reported-state">
                {t("installationPage.tabs.reportedState")}
              </Tab>
            </TabList>
            <TabPanel value="devices">
              <DevicesTab ref={deviceListRef} />
            </TabPanel>
            <TabPanel value="reported-state">
              <ReportedStateTab />
            </TabPanel>
          </Tabs>
        </ListTile>
      </Screen>

      <CoreSetupInstallation
        isModal
        show={show}
        accountId={accountId}
        installationId={installationId}
        onClose={() => setShow(false)}
        onCompleted={async (accountId, installationId, deviceId) => {
          if (deviceListRef.current) {
            deviceListRef.current?.refetch();
          }

          navigate(
            routes.device.set(
              accountId,
              installationId,
              deviceId,
              DeviceTabTypeEnum.Overview,
            ),
          );
        }}
        termsAndConditionsUrl={import.meta.env.VITE_TERMS_AND_CONDITIONS_URL}
        permissions={[
          CorePermissionsEnum.CREATE,
          CorePermissionsEnum.DELETE,
          CorePermissionsEnum.UPDATE,
        ]}
      />

      {ruleState.authenticationCode &&
        ruleState.deviceTwinId &&
        ruleState.installationId && (
          <CoreDeviceRule
            show={showWibutlerRuleWidget}
            wibutlerAuthorizationCode={ruleState.authenticationCode}
            deviceTwinId={ruleState.deviceTwinId}
            installationId={ruleState.installationId}
            onRedirectToAppStore={(productId: string) =>
              navigate(routes.product.set(productId))
            }
            wibutlerCallbackUrl={getCallbackUrl()}
            onClose={() => setShowWibutlerRuleWidget(false)}
          />
        )}
    </>
  );
}
